/**
 * Unified Follow-Up Selectors — Single Source of Truth
 *
 * Every follow-up view MUST derive its data through these selectors.
 * The canonical dataset is followUpTasks from AppDataContext.
 */

import { isPast, isToday, isTomorrow, parseISO, differenceInHours } from "date-fns";
import type { FollowUpTask } from "./opportunityTypes";
import type { Opportunity } from "./opportunityTypes";
import type { Conversation, Message } from "./communicationTypes";

/* ═══════ CANONICAL TYPES ═══════ */

export type FollowUpEffectiveStatus = "completed" | "overdue" | "pending";
export type FollowUpRiskLevel = "critical" | "high" | "medium" | "low";

export const RISK_CONFIG: Record<FollowUpRiskLevel, { label: string; color: string; bg: string; border: string }> = {
  critical: { label: "Critical", color: "text-destructive", bg: "bg-destructive/10", border: "border-destructive/30" },
  high:     { label: "High",     color: "text-warning",  bg: "bg-warning/10",      border: "border-warning/30" },
  medium:   { label: "Medium",   color: "text-warning",   bg: "bg-warning/10",       border: "border-warning/30" },
  low:      { label: "Low",      color: "text-muted-foreground", bg: "bg-muted/30",  border: "border-border" },
};

/* ═══════ ENRICHED FOLLOW-UP ═══════ */

export interface EnrichedFollowUp {
  task: FollowUpTask;
  effectiveStatus: FollowUpEffectiveStatus;
  hoursOverdue: number;
  risk: FollowUpRiskLevel;
  isDueToday: boolean;
  isDueTomorrow: boolean;
  dealValue: number;
  customerName: string;
  customerPhone: string;
  lastAppointmentResult: string | null;
  opportunity: Opportunity | null;
}

/* ═══════ DERIVATION FUNCTIONS ═══════ */

/** Derive effective status from a follow-up task */
export function deriveFollowUpStatus(task: FollowUpTask): FollowUpEffectiveStatus {
  if (task.status === "completed") return "completed";
  try {
    return isPast(parseISO(task.dueDate)) ? "overdue" : "pending";
  } catch {
    return "pending";
  }
}

/** Derive risk level from a follow-up task */
export function deriveFollowUpRisk(
  task: FollowUpTask,
  effectiveStatus: FollowUpEffectiveStatus,
  hoursOverdue: number,
): FollowUpRiskLevel {
  if (effectiveStatus === "overdue" && hoursOverdue > 48) return "critical";
  if (effectiveStatus === "overdue" && hoursOverdue > 12) return "high";
  if (effectiveStatus === "overdue") return "medium";
  if (task.priority === "high" || task.type === "manager") return "high";
  if (task.priority === "low") return "low";
  return "medium";
}

/** Format overdue duration for display */
export function formatOverdueDuration(hours: number): string {
  if (hours < 1) return "< 1h";
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}

/* ═══════ CORE ENRICHMENT ═══════ */

export interface FollowUpSelectorDeps {
  followUpTasks: FollowUpTask[];
  salesOpportunities: Opportunity[];
  conversations: Conversation[];
  messages: Message[];
}

/**
 * getAllFollowUps — the single enrichment pass.
 * Every other selector filters this output.
 */
export function getAllFollowUps(deps: FollowUpSelectorDeps): EnrichedFollowUp[] {
  const { followUpTasks, salesOpportunities } = deps;
  const now = new Date();

  return followUpTasks.map((task): EnrichedFollowUp => {
    const opp = salesOpportunities.find((o) => o.id === task.opportunityId) || null;
    const effectiveStatus = deriveFollowUpStatus(task);
    const dueDate = (() => { try { return parseISO(task.dueDate); } catch { return now; } })();
    const hoursOverdue = effectiveStatus === "overdue" ? differenceInHours(now, dueDate) : 0;
    const risk = deriveFollowUpRisk(task, effectiveStatus, hoursOverdue);

    let lastAppointmentResult: string | null = null;
    if (opp) {
      if (opp.stage === "Closed Won") lastAppointmentResult = "Sold";
      else if (opp.stage === "Closed Lost") lastAppointmentResult = "No Sale";
      else if (opp.stage === "Follow-Up Needed") lastAppointmentResult = "Needs Follow-Up";
      else if (opp.disposition) lastAppointmentResult = opp.disposition.reason || "No Demo";
    }

    return {
      task,
      effectiveStatus,
      hoursOverdue,
      risk,
      isDueToday: isToday(dueDate),
      isDueTomorrow: isTomorrow(dueDate),
      dealValue: opp?.totalEstimate || 0,
      customerName: opp?.customerName || task.assignedTo,
      customerPhone: opp?.primaryPhone || opp?.customerPhone || "",
      lastAppointmentResult,
      opportunity: opp,
    };
  });
}

/* ═══════ SELECTORS ═══════ */

export function getFollowUpsByRep(all: EnrichedFollowUp[], repName: string): EnrichedFollowUp[] {
  return all.filter((f) => f.task.assignedTo === repName);
}

export function getOverdueFollowUps(all: EnrichedFollowUp[]): EnrichedFollowUp[] {
  return all
    .filter((f) => f.effectiveStatus === "overdue")
    .sort((a, b) => b.hoursOverdue - a.hoursOverdue);
}

export function getDueTodayFollowUps(all: EnrichedFollowUp[]): EnrichedFollowUp[] {
  return all
    .filter((f) => f.isDueToday && f.effectiveStatus !== "completed")
    .sort((a, b) => riskOrder(a.risk) - riskOrder(b.risk));
}

export function getUpcomingFollowUps(all: EnrichedFollowUp[]): EnrichedFollowUp[] {
  return all
    .filter((f) => f.effectiveStatus === "pending" && !f.isDueToday)
    .sort((a, b) => new Date(a.task.dueDate).getTime() - new Date(b.task.dueDate).getTime());
}

export function getCompletedFollowUps(all: EnrichedFollowUp[]): EnrichedFollowUp[] {
  return all
    .filter((f) => f.effectiveStatus === "completed")
    .sort((a, b) => new Date(b.task.completedAt || b.task.dueDate).getTime() - new Date(a.task.completedAt || a.task.dueDate).getTime());
}

export function getCommandCenterFollowUps(all: EnrichedFollowUp[], repName: string): EnrichedFollowUp[] {
  return all
    .filter((f) => f.task.assignedTo === repName)
    .filter((f) => f.effectiveStatus !== "completed")
    .sort((a, b) => riskOrder(a.risk) - riskOrder(b.risk));
}

/** Follow-up stats summary */
export function getFollowUpStats(all: EnrichedFollowUp[]) {
  const overdueCount = all.filter((f) => f.effectiveStatus === "overdue").length;
  const dueTodayCount = all.filter((f) => f.isDueToday && f.effectiveStatus !== "completed").length;
  const pendingCount = all.filter((f) => f.effectiveStatus === "pending").length;
  const completedCount = all.filter((f) => f.effectiveStatus === "completed").length;
  const total = all.length;
  const completionRate = total > 0 ? Math.round((completedCount / total) * 100) : 0;
  const criticalCount = all.filter((f) => f.risk === "critical").length;
  const avgOverdueHrs = overdueCount > 0
    ? Math.round(all.filter((f) => f.effectiveStatus === "overdue").reduce((s, f) => s + f.hoursOverdue, 0) / overdueCount)
    : 0;
  const totalAtRisk = all
    .filter((f) => f.effectiveStatus === "overdue" || f.risk === "critical" || f.risk === "high")
    .reduce((s, f) => s + f.dealValue, 0);

  return { overdueCount, dueTodayCount, pendingCount, completedCount, total, completionRate, criticalCount, avgOverdueHrs, totalAtRisk };
}

/* ═══════ UTILITIES ═══════ */

export function riskOrder(risk: FollowUpRiskLevel): number {
  const order: Record<FollowUpRiskLevel, number> = { critical: 0, high: 1, medium: 2, low: 3 };
  return order[risk];
}
